import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { PageHeader, StatusBadge, HealthBadge } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { X, PencilSimple, Trash, UsersThree } from "@phosphor-icons/react";

const STATUSES = ["Not Started", "Active", "On Hold", "Completed", "Cancelled"];
const DELETABLE_STATUSES = new Set(["Completed", "Cancelled", "On Hold", "Archived"]);

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { project_id: "", name: "", client: "", manager: "", start_date: "", end_date: "", status: "Active", budgeted_hours: 0, assigned_user_ids: [], billable: true };
  const [form, setForm] = useState(empty);
  const [teamFor, setTeamFor] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  const [assignSel, setAssignSel] = useState([]);

  const load = async () => {
    const [p, u] = await Promise.all([api.get("/projects"), api.get("/users/employees?include_admins=true")]);
    setProjects(p.data); setUsers(u.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) await api.patch(`/projects/${editing}`, form);
      else await api.post("/projects", form);
      toast.success("Saved"); setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const edit = (p, e) => { e.stopPropagation(); setEditing(p.id); setForm({ ...p }); setOpen(true); };

  const remove = async (p, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete project ${p.project_id} — "${p.name}"?\n\nThis permanently removes the project. Existing timesheet entries that reference it will remain intact but will no longer link to a project.`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success(`Deleted ${p.project_id}`);
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const openAssign = (p, e) => {
    e.stopPropagation();
    setAssignFor(p);
    const myIds = new Set(users.map((u) => u.id));
    setAssignSel((p.assigned_user_ids || []).filter((id) => myIds.has(id)));
  };
  const toggleAssign = (uid) => {
    setAssignSel((arr) => arr.includes(uid) ? arr.filter((x) => x !== uid) : [...arr, uid]);
  };
  const saveAssign = async () => {
    try {
      await api.post(`/projects/${assignFor.id}/assign`, { user_ids: assignSel });
      toast.success("Team assignment updated");
      setAssignFor(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const openTeam = async (p) => {
    setTeamFor(p); setTeamData(null);
    try { const r = await api.get(`/projects/${p.id}/team`); setTeamData(r.data); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const toggleUser = (uid) => {
    const arr = form.assigned_user_ids || [];
    setForm({ ...form, assigned_user_ids: arr.includes(uid) ? arr.filter((x) => x !== uid) : [...arr, uid] });
  };

  return (
    <div data-testid="admin-projects">
      <PageHeader overline="Portfolio" title="Projects"
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#FF5500] hover:bg-[#E64D00] text-white" data-testid="add-project-btn">+ New Project</Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-head">{editing ? "Edit" : "Create"} Project</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Project ID" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} disabled={!!editing} className="rounded-none" data-testid="pf-id" />
                  <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" data-testid="pf-name" />
                  <Input placeholder="Client" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} className="rounded-none" />
                  <Input placeholder="Manager" value={form.manager} onChange={(e) => setForm({ ...form, manager: e.target.value })} className="rounded-none" />
                  <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-none" />
                  <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-none" />
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="h-10 border border-[#E5E5E5] px-2">
                    {STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                  <Input type="number" placeholder="Budgeted Hours" value={form.budgeted_hours} onChange={(e) => setForm({ ...form, budgeted_hours: parseFloat(e.target.value) || 0 })} className="rounded-none" data-testid="pf-budget" />
                </div>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.billable} onChange={(e) => setForm({ ...form, billable: e.target.checked })} /> Billable project</label>
                <div>
                  <div className="eyebrow mb-2">Assigned Employees &amp; Admins</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto border border-[#E5E5E5] p-2">
                    {users.map((u) => (
                      <label key={u.id} className="text-xs flex items-center gap-1" data-testid={`project-user-${u.id}`}>
                        <input type="checkbox" checked={(form.assigned_user_ids || []).includes(u.id)} onChange={() => toggleUser(u.id)} /> {u.name}
                        {u.role === "admin" && <span className="text-[10px] font-semibold text-[#1F4E8C]">(Admin)</span>}
                      </label>
                    ))}
                  </div>
                </div>
                <Button onClick={save} className="w-full rounded-none bg-[#FF5500]" data-testid="save-project">Save</Button>
              </div>
            </DialogContent>
          </Dialog>
        } />

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {projects.map((p) => (
          <div key={p.id} className="bg-white border border-[#E5E5E5] p-5 cursor-pointer hover:border-[#1F4E8C] transition-colors duration-200 group" onClick={() => openTeam(p)} data-testid={`project-card-${p.project_id}`}>
            <div className="flex items-start justify-between">
              <div className="eyebrow">{p.project_id}</div>
              <div className="flex items-center gap-2">
                <StatusBadge status={p.status} />
                {p.status !== "Cancelled" && p.status !== "Completed" && Number(p.utilization_pct) >= 80 && (
                  <HealthBadge pct={p.utilization_pct} showLabel={false} />
                )}
                <button onClick={(e) => openAssign(p, e)} className="opacity-0 group-hover:opacity-100 text-neutral-500 hover:text-[#FF5500] transition-opacity duration-150" title="Assign to my team" data-testid={`project-assign-${p.project_id}`}>
                  <UsersThree size={16} />
                </button>
                <button onClick={(e) => edit(p, e)} className="opacity-0 group-hover:opacity-100 text-neutral-500 hover:text-[#1F4E8C] transition-opacity duration-150" data-testid={`project-edit-${p.project_id}`}>
                  <PencilSimple size={16} />
                </button>
                {DELETABLE_STATUSES.has(p.status) && (
                  <button onClick={(e) => remove(p, e)} className="opacity-0 group-hover:opacity-100 text-neutral-500 hover:text-red-600 transition-opacity duration-150" title={`Delete ${p.status.toLowerCase()} project`} data-testid={`project-delete-${p.project_id}`}>
                    <Trash size={16} />
                  </button>
                )}
              </div>
            </div>
            <h3 className="font-head font-semibold text-xl mt-2 mb-1">{p.name}</h3>
            <div className="text-xs text-neutral-500 mb-4">{p.client} · {p.manager}</div>
            <div className="grid grid-cols-4 gap-2 pt-3 border-t border-[#F0F0EE] text-xs">
              <div><div className="eyebrow text-[10px]">Budget</div><div className="font-head font-semibold text-sm">{p.budgeted_hours}h</div></div>
              <div><div className="eyebrow text-[10px]">Actual</div><div className="font-head font-semibold text-sm">{p.actual_hours}h</div></div>
              <div><div className="eyebrow text-[10px]">Left</div><div className="font-head font-semibold text-sm">{p.remaining_hours}h</div></div>
              <div><div className="eyebrow text-[10px]">Util</div><div className="font-head font-semibold text-sm text-[#FF5500]">{p.utilization_pct}%</div></div>
            </div>
            <div className="h-1 bg-[#F0F0EE] mt-3">
              <div className="h-full bg-[#FF5500]" style={{ width: `${Math.min(100, p.utilization_pct || 0)}%` }} />
            </div>
            <div className="text-[11px] text-neutral-500 mt-3 flex items-center gap-1">
              <UsersThree size={14} /> {(p.assigned_user_ids || []).length} members · click to view team
            </div>
          </div>
        ))}
      </div>

      {/* Team view modal */}
      {teamFor && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={() => setTeamFor(null)}>
          <div className="bg-white border border-[#E5E5E5] max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="project-team-modal">
            <div className="p-6 border-b border-[#E5E5E5] bg-[#1F4E8C] text-white flex items-start justify-between">
              <div>
                <div className="eyebrow text-sky-200">{teamFor.project_id} · Team View</div>
                <h2 className="font-head text-3xl font-bold tracking-tight mt-1">{teamFor.name}</h2>
                <div className="text-sm text-sky-100/80 mt-1">{teamFor.client} · Manager: {teamFor.manager}</div>
                <div className="mt-3"><HealthBadge pct={teamFor.utilization_pct} /></div>
              </div>
              <button onClick={() => setTeamFor(null)} className="text-white/80 hover:text-white" data-testid="close-team-modal"><X size={22} /></button>
            </div>

            {!teamData ? (
              <div className="p-10 text-center text-neutral-500">Loading…</div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-6 bg-[#F7F7F5] border-b border-[#E5E5E5]">
                  <div><div className="eyebrow">Budget</div><div className="font-head text-3xl font-light">{teamData.project.budgeted_hours}h</div></div>
                  <div><div className="eyebrow">Actual</div><div className="font-head text-3xl font-light">{teamData.project.actual_hours}h</div></div>
                  <div><div className="eyebrow">Remaining</div><div className="font-head text-3xl font-light">{teamData.project.remaining_hours}h</div></div>
                  <div><div className="eyebrow">Utilization</div><div className="font-head text-3xl font-light text-[#FF5500]">{teamData.project.utilization_pct}%</div></div>
                </div>
                <div className="p-6">
                  <div className="flex items-end justify-between mb-3">
                    <div>
                      <div className="eyebrow">Members · This Week</div>
                      <div className="text-xs text-neutral-500 mt-1">{teamData.week_from} → {teamData.week_to}</div>
                    </div>
                    <div className="text-sm text-neutral-500">{teamData.members.length} assigned</div>
                  </div>
                  <table className="w-full table-dense">
                    <thead>
                      <tr className="border-b-2 border-[#1F4E8C] text-left">
                        <th className="eyebrow">Member</th><th className="eyebrow">Email</th>
                        <th className="eyebrow">This Week</th><th className="eyebrow">All Time</th><th className="eyebrow">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teamData.members.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-neutral-500">No members assigned to this project</td></tr>}
                      {teamData.members.map((m) => (
                        <tr key={m.id} className="border-b border-[#F0F0EE]">
                          <td className="font-medium">
                            {m.name}
                            <div className="text-xs text-neutral-500">{m.department} · {m.designation}</div>
                          </td>
                          <td className="text-xs">{m.email}</td>
                          <td className="font-head font-semibold text-[#1F4E8C]">{m.this_week_hours}h</td>
                          <td className="font-head font-semibold">{m.total_hours}h</td>
                          <td><StatusBadge status={m.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* Assign to my team modal */}
      {assignFor && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={() => setAssignFor(null)}>
          <div className="bg-white border border-[#E5E5E5] max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="assign-team-modal">
            <div className="p-5 border-b border-[#E5E5E5] flex items-start justify-between">
              <div>
                <div className="eyebrow text-neutral-400">{assignFor.project_id} · Assign to my team</div>
                <h3 className="font-head text-xl font-semibold mt-1">{assignFor.name}</h3>
                <div className="text-xs text-neutral-500 mt-1">Select which of your team members work on this project.</div>
              </div>
              <button onClick={() => setAssignFor(null)} className="text-neutral-400 hover:text-neutral-700" data-testid="close-assign-modal"><X size={20} /></button>
            </div>
            <div className="p-5">
              {users.length === 0 ? (
                <div className="text-sm text-neutral-500">No team members available to assign.</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
                  {users.map((u) => (
                    <label key={u.id} className={`flex items-center gap-2 text-sm border px-3 py-2 cursor-pointer ${assignSel.includes(u.id) ? "border-[#FF5500] bg-[#FFF6F0]" : "border-[#E5E5E5]"}`} data-testid={`assign-user-${u.id}`}>
                      <input type="checkbox" checked={assignSel.includes(u.id)} onChange={() => toggleAssign(u.id)} />
                      <span className="truncate">{u.name}</span>
                      {u.role === "admin" && <span className="text-[10px] font-semibold text-[#1F4E8C]">(Admin)</span>}
                    </label>
                  ))}
                </div>
              )}
              <div className="flex justify-end gap-2 mt-5">
                <Button variant="outline" onClick={() => setAssignFor(null)} className="rounded-none">Cancel</Button>
                <Button onClick={saveAssign} className="rounded-none bg-[#FF5500] hover:bg-[#E64D00] text-white" data-testid="save-assign">Save Assignment</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
